import { useCallback, useMemo, useState } from "react";
import {
  TbBrandWhatsapp,
  TbPlayerPlayFilled,
  TbPlugConnected,
} from "react-icons/tb";
import styles from "../_shared/productLanding.module.css";
import { Link } from "react-router-dom";
import PlansComponent from "../../components/Plans/PlansComponent";
import SeoHead from "../../components/Seo/SeoHead";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";
import SegmentCard from "../../components/Marketing/SegmentCard";
import { SEGMENT_ICONS } from "../../components/Marketing/segmentIcons";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import FaqAccordion from "../../components/Marketing/FaqAccordion";
import TestimonialBlock from "../../components/Marketing/TestimonialBlock";
import VideoSection from "../../components/VideoSection/VideoSection";
import VideoModal from "../../components/VideoModal/VideoModal";
import {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  trackTrialCtaClick,
} from "../../analytics";

// HeroBanner e HealthBeautySection continuam existindo mas não são usados aqui
// para evitar o H1 duplicado e a estrutura genérica

const WHATSAPP_PHONE = "5551992747402";

/** Contexto fixo desta página para o funil de teste grátis (GA4). */
const CTA_CTX = {
  cta_page: CTA_PAGES.AGENDAMENTO,
  business_segment: BUSINESS_SEGMENTS.GENERAL,
  destination: DESTINATIONS.LOGIN_SIGNUP,
  // Quem chega por esta página JÁ escolheu o produto: os CTAs vão direto
  // ao cadastro, sem passar pelo modal da home.
  product: TRIAL_PRODUCTS.AGENDAMENTO,
};

/** Destino dos CTAs — carrega o produto para o signup escolher o plano certo. */
const SIGNUP_PATH = buildTrialSignupPath(TRIAL_PRODUCTS.AGENDAMENTO);

const YOUTUBE_ID = "lkcbXYnJAR8"; // vídeo de apresentação — https://youtu.be/lkcbXYnJAR8

const SEGMENTS = [
  {
    key: "barbearias",
    title: "Barbearias",
    pain: "Pare de interromper o corte pra atender o telefone",
    href: "/barbearias",
  },
  {
    key: "consultorios",
    title: "Consultórios",
    pain: "Confirme consultas sem secretária no telefone",
    href: "/consultorios",
  },
  {
    key: "mecanicas",
    title: "Mecânicas",
    pain: "Receba pedidos de orçamento e agende revisão sozinho",
    href: "/mecanicas",
  },
  {
    key: "lava-jatos",
    title: "Lava-jatos",
    pain: "Agenda lotada mesmo no fim de semana de chuva",
    href: "/lava-jatos",
  },
  {
    key: "academias",
    title: "Academias",
    pain: "Matrículas e aulas agendadas sem fila na recepção",
    href: "/academias",
    badge: "Integra com NextFit",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Conecte seu WhatsApp",
    text: "Em menos de 5 minutos, seu número já responde automaticamente.",
  },
  {
    step: "2",
    title: "Escolha o fluxo do seu segmento",
    text: "Cada segmento tem um fluxo pronto. Não precisa criar do zero.",
  },
  {
    step: "3",
    title: "Receba clientes agendados sozinho",
    text: "O bot agenda, confirma e lembra o cliente. Você só atende.",
  },
];

const FEATURES = [
  {
    title: "Agenda que se organiza sozinha",
    text: "Você só atende quem chega — o bot cuida do resto.",
  },
  {
    title: "Cliente agenda sozinho, mesmo às 23h de domingo",
    text: "Sem depender de você estar online. O bot atende 24/7.",
  },
  {
    title: "Lembretes automáticos que reduzem faltas",
    text: "Confirmação automática antes do horário — clientes que esquecem viram exceção, não regra.",
  },
];
/**
 * Conversa do mockup. Precisa demonstrar uma RESERVA COMPLETA — esta é a página
 * que vende agenda, e um mockup que termina em "nossa equipe entra em contato"
 * demonstra encaminhamento, não agendamento. Genérica de propósito: sem citar
 * segmento, porque a página fala com os cinco.
 */
const DEMO_MESSAGES = [
  { type: "out", text: "Olá, quero agendar um horário para amanhã à tarde." },
  {
    type: "in",
    text: "Claro! Para qual serviço você gostaria de agendar?",
  },
  { type: "out", text: "Avaliação." },
  {
    type: "in",
    text: "Tenho horários às 14h, 15h30 e 17h. Qual fica melhor para você?",
  },
  { type: "out", text: "15h30." },
  {
    type: "in",
    text: "✅ Agendado para amanhã às 15h30. Vou lembrar você antes do horário.",
  },
];

/** O que o cliente resolve sozinho, sem falar com ninguém do seu time. */
const CLIENT_ACTIONS = [
  {
    title: "Consultar os horários livres",
    text: "Ele pede o dia que prefere e recebe de volta o que ainda está aberto na sua agenda.",
  },
  {
    title: "Escolher o profissional",
    text: "Quando mais de uma pessoa atende, ele pede pelo nome e a Nexus confere a agenda daquela pessoa.",
  },
  {
    title: "Marcar o horário",
    text: "A conversa termina com o horário reservado no seu painel — não com um “depois a gente confirma”.",
  },
  {
    title: "Remarcar sem ligar",
    text: "Mudou o plano do dia, ele pede outro horário na mesma conversa e a agenda se ajusta.",
  },
  {
    title: "Cancelar quando precisar",
    text: "O horário volta a ficar livre na hora, e não fica ocupando espaço à toa na sua agenda.",
  },
  {
    title: "Receber a confirmação",
    text: "Assim que marca, ele recebe a mensagem com dia, hora e serviço. Ninguém fica na dúvida do que ficou combinado.",
  },
  {
    title: "Receber o lembrete",
    text: "Antes do horário chega o aviso — dá tempo de confirmar ou remarcar, em vez de simplesmente faltar.",
  },
  {
    title: "Entrar na lista de espera",
    text: "Se o horário que ele quer estiver ocupado, dá para deixá-lo na fila: se alguém cancelar, a Nexus avisa.",
  },
];

const FAQ_ITEMS = [
  {
    question: "E se eu já uso caderno, planilha ou outra agenda?",
    answer:
      "A agenda passa a viver no painel da Nexus, e é nela que o bot escreve — assim ele nunca marca em cima de um horário ocupado. Você e sua equipe continuam vendo e editando tudo por lá, pelo computador ou pelo celular.",
  },
  {
    question: "O cliente consegue escolher o profissional?",
    answer:
      "Sim. Ele pede pelo nome e o bot confere a agenda daquela pessoa. Se o profissional escolhido estiver ocupado no horário pedido, o bot oferece os horários livres dele e também avisa quais outros profissionais estão disponíveis naquele mesmo horário.",
  },
  {
    question: "E se o horário que o cliente quer já estiver ocupado?",
    answer:
      "O bot oferece os horários livres mais próximos e, se você quiser, coloca o cliente na lista de espera daquele horário — se alguém cancelar, ele avisa o cliente automaticamente. Ninguém fica sem resposta e você não perde o atendimento.",
  },
  {
    question: "O bot lembra o cliente antes do horário?",
    answer:
      "Lembra, automaticamente, sem você fazer nada. É o que mais reduz falta: quem esqueceu recebe o aviso a tempo de confirmar ou remarcar.",
  },
  {
    question: "Vocês atendem outros segmentos além desses 5?",
    answer:
      "Hoje a gente foca em cinco — barbearias, consultórios, mecânicas, lava-jatos e academias — pra entregar a melhor experiência em cada nicho. Outros estão no roadmap; se o seu não está na lista, chama a gente que a conversa é rápida.",
  },
  {
    question: "Posso cancelar quando quiser?",
    answer:
      "Sim. Sem fidelidade, sem multa e sem taxa de adesão — você cancela pelo painel. O teste de 14 dias também não pede cartão.",
  },
];

const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Nexus Chatbot",
  url: "https://nexustech.tec.br",
  logo: "https://nexustech.tec.br/logo_nexus_sem_fundo.png",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+55-51-99274-7402",
    contactType: "customer service",
    email: "contato@nexustech.tec.br",
  },
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_ITEMS.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const Home = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const openVideo = useCallback(() => setVideoOpen(true), []);
  const closeVideo = useCallback(() => setVideoOpen(false), []);

  const waHref = useMemo(() => {
    const page =
      typeof window !== "undefined"
        ? window.location.href
        : "https://nexustech.tec.br";
    const text = `Olá! Gostaria de falar com a equipe da Nexus Tech via site. \n\nPágina:${page}`;
    return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(text)}`;
  }, []);

  return (
    <>
      <SeoHead
        title="Agendamento Automático pelo WhatsApp com IA | Nexus"
        description="Seus clientes agendam, remarcam e cancelam pelo WhatsApp, escolhem o profissional e recebem lembretes automáticos. Teste grátis por 14 dias."
        canonical="https://nexustech.tec.br/agendamento"
        ogImage="https://nexustech.tec.br/og-home.jpg"
        // O `FAQPage` volta a ser emitido porque as três páginas passaram a ter
        // perguntas PRÓPRIAS. Enquanto o conjunto era idêntico nas três, três
        // URLs declarando a mesma marcação faziam o Google escolher uma e
        // descartar o resto — mesmo efeito do canonical duplicado. Se um dia
        // alguém copiar o FAQ de uma página para a outra, este comentário é o
        // aviso de que a marcação precisa sair junto.
        jsonLd={[ORG_LD, FAQ_LD]}
      />

      <main className={styles.main} role="main">
        {/* ── SEÇÃO 1: HERO ── */}
        <section className={styles.heroSection}>
          <div className={`${styles.contentWide} ${styles.heroLayout}`}>
            <div className={styles.heroText}>
              <h1 className={styles.heroH1}>
                Agendamento automático pelo WhatsApp, sem depender de você
              </h1>
              <p className={styles.heroH1Subtext}>
                Seu cliente conversa pelo WhatsApp como já conversa com
                qualquer pessoa, vê os horários livres e marca sozinho. Não
                precisa instalar aplicativo nenhum.
              </p>
              <p className={styles.heroSubtitle}>
                Ele escolhe o profissional quando faz sentido, remarca ou
                cancela na mesma conversa e recebe confirmação e lembrete
                automáticos. Tudo cai na agenda da Nexus, que você e sua equipe
                acompanham pelo painel.
              </p>

              <div className={styles.heroCtas}>
                <Link
                  to={SIGNUP_PATH}
                  className={styles.ctaPrimary}
                  onClick={() =>
                    trackTrialCtaClick({
                      ...CTA_CTX,
                      cta_placement: CTA_PLACEMENTS.HERO,
                    })
                  }
                >
                  Começar meu teste grátis
                </Link>
                <a href="#segmentos" className={styles.ctaSecondary}>
                  Ver soluções por segmento
                </a>
                {/* Sem aria-label: o nome acessível é o texto visível
                    ("Ver apresentação") — WCAG 2.5.3 Label in Name. */}
                <button
                  type="button"
                  className={styles.ctaWatch}
                  onClick={openVideo}
                >
                  <TbPlayerPlayFilled
                    className={styles.ctaWatchIcon}
                    aria-hidden="true"
                  />
                  Ver apresentação
                </button>
              </div>

              <p className={styles.heroCopy}>
                Sem cartão. Cancele quando quiser. Configuração em 15 minutos.
              </p>
            </div>

            <div className={styles.heroMockup}>
              <WhatsAppMockup messages={DEMO_MESSAGES} looping />
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 1.5: VÍDEO DE APRESENTAÇÃO ── */}
        <VideoSection onPlay={openVideo} />

        {/* ── SEÇÃO 1.6: O QUE É (pilar de busca long-tail) ── */}
        <section className={styles.plainSection}>
          <div className={styles.contentNarrow}>
            <h2 className={styles.sectionHeading}>
              O que é agendamento automático pelo WhatsApp?
            </h2>
            <p className={styles.sectionSubtitle}>
              É o seu cliente marcar horário conversando no aplicativo que ele
              já usa todo dia, sem ligar, sem esperar alguém ficar livre para
              responder e sem abrir outro site. Ele escreve o que precisa, a
              Nexus entende o pedido e conduz a conversa até o horário ficar
              marcado.
            </p>
            <p className={styles.sectionSubtitle}>
              A diferença está no que acontece antes de confirmar: a Nexus
              consulta a disponibilidade da sua agenda e só oferece horário que
              está de fato livre. Nada de marcar duas pessoas no mesmo horário
              nem de prometer encaixe que não existe. Se o horário pedido já
              estiver ocupado, ela oferece as opções mais próximas.
            </p>
          </div>
        </section>

        {/* ── SEÇÃO 2: PARA QUEM É ── */}
        <section id="segmentos" className={styles.tintedSection}>
          <div className={styles.contentWide}>
            <h2 className={styles.sectionHeading}>
              Feito para o seu segmento, não genérico
            </h2>
            <p className={styles.sectionSubtitle}>
              Soluções pensadas para quem atende com hora marcada.
            </p>
            <div className={styles.segmentGrid}>
              {SEGMENTS.map((seg) => (
                <SegmentCard
                  key={seg.key}
                  Icon={SEGMENT_ICONS[seg.key]}
                  title={seg.title}
                  pain={seg.pain}
                  href={seg.href}
                  badge={seg.badge}
                />
              ))}
            </div>

            {/* NextFit note — discreto, abaixo do grid de segmentos */}
            <div className={styles.nextfitNote}>
              <span className={styles.nextfitIcon} aria-hidden="true">
                <TbPlugConnected />
              </span>
              <div>
                <h3 className={styles.nextfitTitle}>
                  Já usa NextFit? O bot conversa com ele.
                </h3>
                <p className={styles.nextfitText}>
                  O bot consulta (somente leitura) agenda, aulas, planos e
                  contratos do seu NextFit.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 3: COMO FUNCIONA ── */}
        <section className={styles.plainSection}>
          <div className={styles.contentNarrow}>
            <h2 className={styles.sectionHeading}>
              Configuração em 15 minutos. Funciona em segundos.
            </h2>

            <div className={styles.stepsGrid}>
              {HOW_IT_WORKS.map((item) => (
                <div key={item.step} className={styles.step}>
                  <div className={styles.stepCircle}>{item.step}</div>
                  <h3 className={styles.stepTitle}>{item.title}</h3>
                  <p className={styles.stepText}>{item.text}</p>
                </div>
              ))}
            </div>

            <div className={styles.ctaBlock}>
              <Link
                to={SIGNUP_PATH}
                className={styles.ctaPrimary}
                onClick={() =>
                  trackTrialCtaClick({
                    ...CTA_CTX,
                    cta_placement: CTA_PLACEMENTS.MIDDLE,
                  })
                }
              >
                Quero usar no meu negócio
              </Link>
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 4: RECURSOS ── */}
        <section className={styles.tintedSection}>
          <div className={styles.contentNarrow}>
            <h2 className={styles.sectionHeading}>
              Tudo o que sua agenda precisa, num só chat
            </h2>

            <div className={styles.featuresGrid}>
              {FEATURES.map((feat) => (
                <div key={feat.title} className={styles.featureCard}>
                  <h3 className={styles.featureTitle}>{feat.title}</h3>
                  <p className={styles.featureText}>{feat.text}</p>
                </div>
              ))}
            </div>

            <div className={styles.ctaBlock}>
              <Link
                to={SIGNUP_PATH}
                className={styles.ctaPrimary}
                onClick={() =>
                  trackTrialCtaClick({
                    ...CTA_CTX,
                    cta_placement: CTA_PLACEMENTS.FEATURES,
                  })
                }
              >
                Ativar meu chatbot agora
              </Link>
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 4.5: O QUE O CLIENTE FAZ SOZINHO ── */}
        <section className={styles.plainSection}>
          <div className={styles.contentNarrow}>
            <h2 className={styles.sectionHeading}>
              O que seu cliente consegue fazer sozinho
            </h2>

            <div className={styles.featuresGrid}>
              {CLIENT_ACTIONS.map((action) => (
                <div key={action.title} className={styles.featureCard}>
                  <h3 className={styles.featureTitle}>{action.title}</h3>
                  <p className={styles.featureText}>{action.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 4.6: LINK OU CONVERSA ── */}
        <section className={styles.tintedSection}>
          <div className={styles.contentNarrow}>
            <h2 className={styles.sectionHeading}>
              Agendamento por link ou por conversa?
            </h2>
            <p className={styles.sectionSubtitle}>
              Link de agendamento funciona, e muita gente resolve assim. A
              diferença está em onde fica o esforço: com um link, o cliente
              precisa sair da conversa, abrir uma página e navegar sozinho pela
              grade de horários até o fim. Se surgir uma dúvida no meio — se
              atende no sábado, quanto custa, quanto tempo demora — ele volta ao
              WhatsApp, pergunta e depois recomeça.
            </p>
            <p className={styles.sectionSubtitle}>
              Na Nexus ele não sai do lugar. Continua no WhatsApp e é conduzido
              durante a conversa: pergunta o que precisa, recebe a resposta e
              marca o horário no mesmo atendimento. Dúvida e agendamento
              acontecem juntos, e não em dois lugares diferentes.
            </p>
            <p className={styles.sectionSubtitle}>
              Se o seu negócio não trabalha com hora marcada e o que você
              precisa é responder o cliente, existe um plano mais simples só
              para isso.
            </p>

            <div className={styles.ctaBlock}>
              {/* Sem aria-label: o nome acessível é o texto visível
                  — WCAG 2.5.3 Label in Name. */}
              <Link to="/atendimento#planos" className={styles.ctaSecondary}>
                Ver planos de atendimento sem agendamento
              </Link>
            </div>
          </div>
        </section>

        {/* ── SEÇÃO 5: PROVA SOCIAL ── */}
        {/* TestimonialBlock retorna null se array vazio — não renderiza placeholder */}
        <TestimonialBlock testimonials={[]} />

        {/* ── SEÇÃO 6: PRICING ── */}
        <section id="planos" className={styles.plainSection}>
          <div className={styles.contentWide}>
            <h2 className={styles.sectionHeading}>
              Planos que cabem no caixa de quem tá começando
            </h2>
            <p className={styles.pricingSubtitle}>
              Sem contrato. Sem taxa de adesão. Cancele quando quiser.
            </p>
            <PlansComponent />
          </div>
        </section>

        {/* ── SEÇÃO 7: FAQ ── */}
        <section className={styles.tintedSection}>
          <div className={styles.contentNarrowest}>
            <h2 className={styles.sectionHeading}>
              Perguntas que todo mundo faz
            </h2>
            <FaqAccordion items={FAQ_ITEMS} />
            {/* Bloco E: botão "Falar no WhatsApp" removido — FAB já é onipresente */}
          </div>
        </section>

        {/* ── CTA FINAL ── */}
        <section className={styles.ctaFinalSection}>
          <div className={styles.contentCenter}>
            <h2 className={styles.ctaFinalHeading}>
              Pronto para deixar de atender telefone manualmente?
            </h2>
            <p className={styles.ctaFinalText}>
              14 dias grátis. Sem cartão. Cancelamento a qualquer momento.
            </p>
            <div className={styles.ctaFinalCtas}>
              <Link
                to={SIGNUP_PATH}
                className={styles.ctaFinalBtn}
                onClick={() =>
                  trackTrialCtaClick({
                    ...CTA_CTX,
                    cta_placement: CTA_PLACEMENTS.FINAL,
                  })
                }
              >
                Começar grátis por 14 dias
              </Link>
              <a
                href={waHref}
                className={styles.ctaFinalWhats}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TbBrandWhatsapp aria-hidden="true" />
                Falar no WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* ── WHATSAPP FAB (fixo) ── */}
        <a
          href={waHref}
          className={styles.whatsappFab}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir conversa no WhatsApp"
        >
          <TbBrandWhatsapp className={styles.whatsappIcon} aria-hidden="true" />
          <span className={styles.srOnly}>Conversar no WhatsApp</span>
        </a>
      </main>

      {/* Modal do vídeo — fora do <main> por ser conteúdo sobreposto */}
      <VideoModal
        open={videoOpen}
        youtubeId={YOUTUBE_ID}
        onClose={closeVideo}
      />
    </>
  );
};

export default Home;
